import { useState, useEffect, useMemo, useCallback } from "react";
import { Checkbox, Divider, Upload } from "antd";
import {
  ContainerSelection,
  DeleteIcon,
  DocumentSelect,
  DownloadTemplateButton,
  ErrorMessage,
  FileIcon,
  FileItemContainer,
  FileItemContent,
  NotesOptional,
  UploadIcon,
  UploadWrapper,
  ModalBtnsContainer,
  ModalActionBtns,
  ModalTitle,
  SendModal,
  DraggerText,
  FileNameSpan,
  ExtensionInformation,
  ObsContainer,
  ObsInput,
  ClickHere,
  UploadersScrollArea,
} from "./styles";
import { useIntl } from "react-intl";
import api from "../../../service";
import { useSelector, useDispatch } from "react-redux";
import { checkMandatoryDocuments } from "../../../redux/modules/documentRequired/actions";
import useNotification from "../../../hooks/newUXNotification";
import axios from "axios";
import { IoMdDownload } from "react-icons/io";
import { getDocumentConfigsService } from "../../../service/uploadFlow.service";
import {
  getUploadMassStatusReportService,
  uploadMassStatusReportFormService,
} from "../../../service/approvalDocuments.service";

const MAX_OBSERVATION_LENGTH = 255;
const NO_EXTENSION_PLACEHOLDER = "-";
const SINGLE_KEY = "single";
const CREATE_UPLOAD_PATH = "/upload-flow/create-upload";
const VALIDATE_UPLOAD_PATH = "/upload-flow/validate-upload";
const DOCUMENT_PATH = (documentId) => `/upload-flow/documents/${documentId}`;

/** Remove acentos para compatibilidade com S3 */
const removeAccents = (str) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const getFileExtension = (fileName = "") => fileName.split(".").pop();

const AdminSendFileModal = ({
  visible,
  onFinish,
  documentId,
  documentName,
  opportunityId,
  eventId,
  allowedFileExtensions = NO_EXTENSION_PLACEHOLDER,
  accountId,
  serviceProviderId,
  flowName,
  thirdPartyUpload = false,
  isBulkUpload = false,
  selectEvent = null,
  packageDocuments = [],
}) => {
  const { Dragger } = Upload;
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const notification = useNotification();

  // Mapas por key do uploader
  const [fileLists, setFileLists] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [documentError, setDocumentError] = useState("");
  const [documentTouched, setDocumentTouched] = useState(false);
  const [observation, setObservation] = useState(
    intl.formatMessage({ id: "send_made_by_partner" })
  );
  const [autoApprove, setAutoApprove] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const isPackage =
    Array.isArray(packageDocuments) && packageDocuments.length > 0;
  // Um uploader por documento do pacote, ou único para single/bulk
  const uploaders = useMemo(() => {
    if (
      isPackage &&
      Array.isArray(packageDocuments) &&
      packageDocuments.length > 0
    ) {
      return packageDocuments.map((doc) => ({
        key: String(doc.documentConfigId ?? doc.id),
        documentConfigId: doc.documentConfigId ?? doc.id,
        name: doc.fileName ?? documentName,
        fileTypes: doc.fileTypes ?? [], // ← extensões específicas de cada documento
      }));
    }
    return [
      { key: SINGLE_KEY, documentConfigId: documentId, name: documentName },
    ];
  }, [isPackage, packageDocuments, documentId, documentName]);

  // Calcula extensões aceitas a partir de um array ou string de tipos
  const buildAcceptExtensions = useCallback((fileTypes) => {
    if (!fileTypes) return undefined;
    const extList = Array.isArray(fileTypes)
      ? fileTypes
      : String(fileTypes).split(",").filter(Boolean);
    if (!extList.length) return undefined;
    return extList
      .map((ext) => {
        const trimmed = ext.trim();
        return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
      })
      .join(",");
  }, []);

  const acceptExtensions = useMemo(() => {
    if (
      !allowedFileExtensions ||
      allowedFileExtensions === NO_EXTENSION_PLACEHOLDER
    ) {
      return undefined;
    }
    return allowedFileExtensions
      .split(",")
      .map((ext) => {
        const trimmed = ext.trim();
        return trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
      })
      .join(",");
  }, [allowedFileExtensions]);

  const isFormValid = () => {
    const allFilesValid = uploaders.every(({ key }) => {
      const list = fileLists[key];
      return list?.length > 0 && list[0]?.originFileObj;
    });
    if (isBulkUpload) return allFilesValid && selectedDocument !== null;
    return allFilesValid;
  };

  const handleShowValidationErrors = () => {
    const errors = {};
    uploaders.forEach(({ key }) => {
      const list = fileLists[key];
      if (!list?.length || !list[0]?.originFileObj) {
        errors[key] = intl.formatMessage({ id: "select_file_required" });
      }
    });
    if (Object.keys(errors).length > 0) setFileErrors(errors);
    if (isBulkUpload && !selectedDocument) {
      setDocumentError(
        intl.formatMessage({ id: "select_flow_document_required" })
      );
      setDocumentTouched(true);
    }
  };

  useEffect(() => {
    if (!visible) {
      setFileErrors({});
      setDocumentError("");
      setDocumentTouched(false);
    }
  }, [visible]);

  useEffect(() => {
    if (visible) setAutoApprove(false);
  }, [visible]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!selectEvent) return;
      try {
        setLoadingDocuments(true);
        const response = await getDocumentConfigsService(
          selectEvent || eventId
        );
        setDocuments(response.data.output || []);
      } catch (error) {
        console.error("Erro ao buscar documentos:", error);
        setDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };
    fetchDocuments();
  }, [visible]);

  const buildAuthHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "*/*",
    Authorization: `Bearer ${token}`,
  });

  // Monta requisição com campos extras do admin (accountId, serviceProviderId, etc.)
  const buildExecutionRequest = ({ documentConfigId, file }) => ({
    opportunityId,
    eventId,
    observation,
    uploadedBy: user.id,
    documentConfigId,
    fileExtension: getFileExtension(file.name),
    accountId,
    serviceProviderId,
    flowName: flowName || documentName,
    thirdPartyUpload,
  });

  const handleRemove = (key, file) => {
    setFileLists((prev) => ({
      ...prev,
      [key]: (prev[key] ?? []).filter((f) => f.uid !== file.uid),
    }));
  };

  const handleChange = (key, fileList) => {
    const newList = fileList.map((file) => {
      if (file.originFileObj && file.name) {
        const ext = file.name.includes(".")
          ? `.${file.name.split(".").pop()}`
          : "";
        const base = file.name.replace(ext, "");
        const cleanName = `${removeAccents(base)}${ext}`;
        if (file.originFileObj.name !== cleanName) {
          const cleanFile = new File([file.originFileObj], cleanName, {
            type: file.originFileObj.type,
          });
          return { ...file, name: cleanName, originFileObj: cleanFile };
        }
      }
      return file;
    });
    setFileLists((prev) => ({ ...prev, [key]: newList }));
    if (newList.length > 0) setFileErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // Envia single ou pacote: array de requisições → backend → S3 → validate (em paralelo)
  const uploadDocuments = async () => {
    const headers = buildAuthHeaders();
    const requests = uploaders.map(({ key, documentConfigId }) =>
      buildExecutionRequest({
        documentConfigId,
        file: fileLists[key][0].originFileObj,
      })
    );

    const responseCreate = await api.post(
      CREATE_UPLOAD_PATH,
      requests,
      headers
    );
    const createdList = Array.isArray(responseCreate.data?.data)
      ? responseCreate.data.data
      : [];

    await Promise.all(
      createdList.map(async (created, index) => {
        const file = fileLists[uploaders[index].key][0].originFileObj;
        try {
          await axios.put(created.url, file, {
            headers: { "Content-Type": file.type },
          });
          await api.put(VALIDATE_UPLOAD_PATH, headers, {
            params: { documentId: created.documentId, autoApprove },
          });
        } catch (err) {
          await api.delete(DOCUMENT_PATH(created.documentId), headers);
          throw err;
        }
      })
    );
  };

  const handleSendClick = async () => {
    try {
      const errors = {};
      uploaders.forEach(({ key }) => {
        const list = fileLists[key];
        if (!list?.length || !list[0]?.originFileObj) {
          errors[key] = intl.formatMessage({ id: "select_file_required" });
        }
      });

      if (Object.keys(errors).length > 0) {
        setFileErrors(errors);
        return;
      }

      setFileErrors({});
      setIsUploading(true);

      await uploadDocuments();

      notification.displaySuccess(
        intl.formatMessage({
          id: isPackage
            ? "package_uploaded_successfully"
            : "document_uploaded_successfully",
        }),
        "document-uploaded-success-message"
      );
      dispatch(
        checkMandatoryDocuments(
          opportunityId,
          () => {},
          () => {}
        )
      );

      setFileLists({});
      setObservation(intl.formatMessage({ id: "send_made_by_partner" }));
      setIsUploading(false);
      setFileErrors({});
      onFinish({ success: true });
    } catch (err) {
      setIsUploading(false);
      const errorCode = err?.response?.data?.code;
      notification.displayError(
        intl.formatMessage(
          {
            id: errorCode ? `api.error.${errorCode}` : "document_upload_error",
          },
          { allowedFileExtensions }
        ),
        "document-upload-error-message"
      );
    }
  };

  const OnSubmitUploadMass = async () => {
    const firstKey = uploaders[0]?.key ?? SINGLE_KEY;
    const firstList = fileLists[firstKey];

    if (!firstList?.length || !firstList[0]?.originFileObj) {
      setFileErrors({
        [firstKey]: intl.formatMessage({ id: "select_file_required" }),
      });
      return;
    }

    if (!selectedDocument) {
      setDocumentError(
        intl.formatMessage({ id: "select_flow_document_required" })
      );
      setDocumentTouched(true);
      return;
    }

    setFileErrors({});
    setDocumentError("");

    const formData = new FormData();
    formData.append("sheet", firstList[0].originFileObj);
    formData.append("flowName", selectedDocument.flowName);
    formData.append("eventId", selectEvent || eventId);
    formData.append("uploadedBy", user.id);
    formData.append("documentConfigId", selectedDocument.documentConfigId);
    formData.append("thirdPartyUpload", String(thirdPartyUpload));
    formData.append("autoApprove", String(autoApprove));
    formData.append("observation", observation);

    try {
      setIsUploading(true);
      const response = await uploadMassStatusReportFormService(token, formData);
      const { reportId, error } = response.data;

      if (error || !reportId) throw new Error("Erro ao iniciar processamento");

      await pollUploadStatus(reportId);

      notification.displaySuccess(
        intl.formatMessage({ id: "document_uploaded_successfully" }),
        "document-uploaded-success-message"
      );
      onFinish({ success: true });
    } catch (error) {
      const errorCode = error.response?.data?.code;
      notification.displayError(
        intl.formatMessage(
          { id: errorCode ? `api.error.${errorCode}` : "error.generic" },
          { allowedFileExtensions }
        ),
        "document-creation-error-message"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const pollUploadStatus = async (
    reportId,
    maxAttempts = 20,
    interval = 5000
  ) => {
    let attempts = 0;
    while (attempts < maxAttempts) {
      const response = await getUploadMassStatusReportService(token, reportId);
      const { status, downloadUrl } = response.data;
      if (status === "PROCESSED") {
        if (downloadUrl) await downloadFile(downloadUrl);
        return response.data;
      }
      if (status === "ERROR") throw new Error("PROCESSING_ERROR");
      if (status === "WAITING" || status === "PENDING") {
        await new Promise((resolve) => setTimeout(resolve, interval));
        attempts++;
        continue;
      }
      throw new Error(`UNKNOWN_STATUS_${status}`);
    }
    throw new Error("TIMEOUT");
  };

  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/modelo-importacao-apolices.xlsx";
    link.download = "modelo-importacao-apolices.xlsx";
    link.click();
  };

  const handleCancel = () => {
    onFinish({ success: false });
    setFileLists({});
    setAutoApprove(false);
    setObservation(intl.formatMessage({ id: "send_made_by_partner" }));
    setIsUploading(false);
    setFileErrors({});
    setDocumentError("");
    setDocumentTouched(false);
    setSelectedDocument(null);
  };

  const modalTitle = useMemo(() => {
    const action = intl.formatMessage({
      id: isPackage ? "send_package_documents" : "send_document",
    });
    return flowName ? `${flowName} - ${action}` : action;
  }, [flowName, isPackage, intl]);

  return (
    <SendModal
      data-test="bulk-upload-modal"
      centered
      closable={false}
      visible={visible}
      footer={[
        <ModalBtnsContainer key="footer">
          <ModalActionBtns
            data-test="bulk-upload-cancel-button"
            $isCancel
            onClick={handleCancel}
            disabled={isUploading}
          >
            {intl.formatMessage({ id: "cancel" })}
          </ModalActionBtns>
          <ModalActionBtns
            data-test="bulk-upload-send-button"
            onClick={() => {
              if (!isFormValid() && !isUploading) {
                handleShowValidationErrors();
                return;
              }
              isBulkUpload ? OnSubmitUploadMass() : handleSendClick();
            }}
            disabled={isUploading || !isFormValid()}
            style={{
              opacity: isUploading || !isFormValid() ? 0.6 : 1,
              cursor: isUploading || !isFormValid() ? "not-allowed" : "pointer",
            }}
          >
            {isUploading
              ? intl.formatMessage({ id: "uploading" })
              : intl.formatMessage({ id: "send" })}
          </ModalActionBtns>
        </ModalBtnsContainer>,
      ]}
    >
      <ModalTitle data-test="bulk-upload-modal-title">{modalTitle}</ModalTitle>

      {isBulkUpload && (
        <>
          <DownloadTemplateButton
            data-test="bulk-upload-download-template-button"
            disabled={isUploading}
            onClick={handleDownloadTemplate}
          
          >
            <IoMdDownload />
            {intl.formatMessage({ id: "download_template" })}
          </DownloadTemplateButton>
          <Divider />
          <div>
            <ContainerSelection>
              <span>{intl.formatMessage({ id: "flow_document" })}</span>
              <DocumentSelect
                data-test="bulk-upload-document-select"
                disabled={isUploading}
                placeholder={intl.formatMessage({ id: "select" })}
                loading={loadingDocuments}
                value={selectedDocument?.value || undefined}
                options={documents.map((doc) => ({
                  label: doc.label,
                  value: doc.value,
                }))}
                onChange={(value) => {
                  const fullObject = documents.find(
                    (doc) => doc.value === value
                  );
                  setSelectedDocument(fullObject);
                  setDocumentError("");
                  setDocumentTouched(true);
                }}
                onBlur={() => {
                  setDocumentTouched(true);
                  if (!selectedDocument) {
                    setDocumentError(
                      intl.formatMessage({
                        id: "select_flow_document_required",
                      })
                    );
                  }
                }}
                status={documentError ? "error" : undefined}
                style={documentError ? { borderColor: "red" } : undefined}
              />
            </ContainerSelection>
            {documentError && documentTouched && (
              <ErrorMessage>{documentError}</ErrorMessage>
            )}
          </div>
        </>
      )}

      {/* Renderiza um uploader por documento (1 para single/bulk, N para pacote) */}
      <UploadersScrollArea>
        {uploaders.map(({ key, name, fileTypes: uploaderFileTypes }) => {
          const list = fileLists[key] ?? [];
          const error = fileErrors[key];

          // Para pacotes: usa extensões específicas de cada documento
          // Para single/bulk: usa as extensões globais do modal
          const currentAcceptExtensions =
            isPackage && uploaderFileTypes?.length
              ? buildAcceptExtensions(uploaderFileTypes)
              : acceptExtensions;

          const displayExtensions =
            isPackage && uploaderFileTypes?.length
              ? Array.isArray(uploaderFileTypes)
                ? uploaderFileTypes.join(", ")
                : String(uploaderFileTypes)
              : allowedFileExtensions;

          return (
            <div key={key}>
              {!isBulkUpload && (
                <p>
                  {intl.formatMessage({ id: "upload" })}: {name}
                  <span style={{ color: "red" }}>*</span>
                </p>
              )}
              <ExtensionInformation>
                {intl.formatMessage(
                  { id: "file_extension_message" },
                  { allowedFileExtensions: displayExtensions }
                )}
              </ExtensionInformation>
              <UploadWrapper>
                <div data-test="bulk-upload-dragger">
                <Dragger
                  disabled={isUploading}
                  required
                  multiple={false}
                  maxCount={1}
                  beforeUpload={() => false}
                  fileList={list}
                  accept={currentAcceptExtensions}
                  onChange={({ fileList: fl }) => handleChange(key, fl)}
                  itemRender={(_, file) => (
                    <FileItemContainer
                      data-test="bulk-upload-file-item"
                      className={isUploading ? "disabled" : ""}
                    >
                      <FileItemContent>
                        <FileIcon />
                        <FileNameSpan>{file.name}</FileNameSpan>
                      </FileItemContent>
                      <DeleteIcon data-test="bulk-upload-delete-icon" onClick={() => handleRemove(key, file)} />
                    </FileItemContainer>
                  )}
                >
                  <UploadIcon />
                  <DraggerText>
                    <ClickHere>
                      {intl.formatMessage({ id: "click_here" })}
                    </ClickHere>
                    {intl.formatMessage({ id: "or_drag_and_drop_file" })}
                  </DraggerText>
                </Dragger>
                </div>
                {error && (
                  <div style={{ color: "red", marginTop: 8, fontSize: 13 }}>
                    {error}
                  </div>
                )}
              </UploadWrapper>
            </div>
          );
        })}
      </UploadersScrollArea>

      <ObsContainer>
        <Checkbox
          data-test="bulk-upload-auto-approve-checkbox"
          disabled={isUploading}
          checked={autoApprove}
          onChange={(e) => setAutoApprove(e.target.checked)}
        >
          <p>{intl.formatMessage({ id: "auto_approve" })}</p>
        </Checkbox>
        <p>
          {intl.formatMessage({ id: "notes" })}{" "}
          <NotesOptional>
            {intl.formatMessage({ id: "notesOptional" })}
          </NotesOptional>
        </p>
        <ObsInput
          data-test="bulk-upload-observation-input"
          disabled={isUploading}
          showCount
          maxLength={MAX_OBSERVATION_LENGTH}
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
        />
      </ObsContainer>
    </SendModal>
  );
};

export default AdminSendFileModal;
